import { useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Check, ChevronDown, X } from "lucide-react";

import { Button } from "@app/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@app/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import { WithheldCount } from "@app/components/WithheldCount";
import { cn } from "@app/lib/utils";
import type { StudyVariable } from "@app/types/study";

import { FAMILY_LABELS, VARIABLE_TYPE_LABELS } from "./labels";

/** Lower case without accents: « Sténose » is found by typing « stenose ». */
function fold(text: string): string {
  let out = "";
  for (const char of text.normalize("NFD")) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x300 || code > 0x36f) out += char;
  }
  return out.toLowerCase();
}

/**
 * The words typed, found in the variable's name. cmdk's default ranking is fuzzy — it scattered the
 * letters of « Score ASA » over « Intensité d'activité sportive » and ranked that first — and a
 * reader typing a field's name expects that field.
 */
function matchScore(value: string, search: string): number {
  const haystack = fold(value);
  const query = fold(search).trim();
  if (!query) return 1;
  if (haystack.startsWith(query)) return 1;
  if (haystack.includes(query)) return 0.8;
  return query.split(/\s+/).every((word) => haystack.includes(word)) ? 0.5 : 0;
}

interface BaseProps {
  variables: StudyVariable[];
  /** Only these may be picked — e.g. no number as an outcome. */
  allow?: (variable: StudyVariable) => boolean;
  placeholder: string;
  className?: string;
}

interface SingleProps extends BaseProps {
  mode: "single";
  value: string | null;
  onChange: (key: string | null) => void;
}

interface MultiProps extends BaseProps {
  mode: "multi";
  value: string[];
  onChange: (keys: string[]) => void;
}

/**
 * A searchable list of the study's variables, grouped by what they describe — the patient, or the
 * act and its episode — since that decides the unit a variable is counted in.
 */
export function VariablePicker(props: SingleProps | MultiProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const allowed = useMemo(
    () => props.variables.filter((v) => (props.allow ? props.allow(v) : true)),
    [props],
  );
  const families = useMemo(
    () =>
      (["patient", "acte"] as const)
        .map((family) => ({
          family,
          items: allowed.filter((v) => v.family === family),
        }))
        .filter((group) => group.items.length > 0),
    [allowed],
  );
  const byKey = useMemo(
    () => new Map(props.variables.map((v) => [v.key, v])),
    [props.variables],
  );
  const selected =
    props.mode === "multi" ? props.value : props.value ? [props.value] : [];

  const toggle = (key: string) => {
    if (props.mode === "single") {
      props.onChange(props.value === key ? null : key);
      setOpen(false);
      return;
    }
    props.onChange(
      props.value.includes(key)
        ? props.value.filter((k) => k !== key)
        : [...props.value, key],
    );
  };

  const triggerText =
    selected.length === 0
      ? props.placeholder
      : props.mode === "single"
        ? (byKey.get(selected[0])?.label ?? selected[0])
        : t`${selected.length} variable(s)`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-between font-normal text-slate-700 hover:border-primary/50",
            selected.length === 0 && "text-slate-500",
            props.className,
          )}
        >
          <span className="truncate">{triggerText}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[26rem] p-0" align="start">
        <Command filter={matchScore}>
          <CommandInput placeholder={t`Rechercher une variable...`} />
          <CommandList className="max-h-80">
            <CommandEmpty>
              <Trans>Aucune variable ne correspond.</Trans>
            </CommandEmpty>
            {families.map((group) => (
              <CommandGroup key={group.family} heading={t(FAMILY_LABELS[group.family])}>
                {group.items.map((variable) => {
                  const isSelected = selected.includes(variable.key);
                  const documented = variable.documented.reduce<number>((a, b) => a + (b ?? 0), 0);
                  const withheld = variable.documented.some((n) => n === null);
                  return (
                    <CommandItem
                      key={variable.key}
                      value={`${variable.label} ${variable.section ?? ""} ${variable.key}`}
                      onSelect={() => toggle(variable.key)}
                      className="flex items-center gap-2"
                    >
                      <Check
                        className={cn(
                          "h-4 w-4 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex-1 truncate">{variable.label}</span>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-2xs text-slate-500">
                        {t(VARIABLE_TYPE_LABELS[variable.type])}
                      </span>
                      {/* A cohort's count under the threshold is withheld: the sum then says so. */}
                      <span className="min-w-12 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                        {withheld && documented === 0 ? (
                          <WithheldCount />
                        ) : (
                          <>
                            {documented}
                            {withheld && (
                              <>
                                {" + "}
                                <WithheldCount />
                              </>
                            )}
                          </>
                        )}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** The chosen variables as removable chips — the picker's summary in the settings bar. */
export function VariableChips({
  variables,
  value,
  onRemove,
}: {
  variables: StudyVariable[];
  value: string[];
  onRemove: (key: string) => void;
}) {
  const { t } = useLingui();
  const byKey = new Map(variables.map((v) => [v.key, v]));
  if (value.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {value.map((key) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-700"
        >
          {byKey.get(key)?.label ?? key}
          <button
            type="button"
            onClick={() => onRemove(key)}
            className="text-slate-400 hover:text-slate-700"
            aria-label={t`Retirer cette variable`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}
